/**
 * Generated 2026 cohort.
 *
 * The hand-written seed carries the demo scenarios (arbitraje, conflictos,
 * tokens de invitación) but only a handful of admissible applications — not
 * enough to exercise a contest that expects ~500. This module generates that
 * volume programmatically so the bulk-assignment wizard, the pagination of the
 * lists and the evaluator quota can be seen at real scale.
 *
 * It is deterministic (everything derives from the index) and additive: the
 * records here are appended to the hand-written ones, which stay untouched.
 */

import { StoredApplication, StoredAssignment, StoredUser } from './seed';

// Local copy of seed's helper: importing the value would create a runtime cycle
// (seed imports this module), and only the types above are erased at compile time.
const DAY_MS = 24 * 60 * 60 * 1000;
const daysFromNow = (days: number): string =>
  new Date(Date.now() + days * DAY_MS).toISOString();

export const COHORT_APPLICATIONS = 500;
export const COHORT_EVALUATORS = 170;
const COHORT_RESEARCHERS = 150;

const T_OPEN = '44444444-4444-4444-8444-000000000001';
const ADMIN_ID = '33333333-3333-4333-8333-000000000003';
const FIRST_FOLIO = 26101;

const FIRST_NAMES = [
  'Alejandra', 'Andrés', 'Bárbara', 'Benjamín', 'Camila', 'Carlos', 'Catalina',
  'Cristóbal', 'Daniela', 'Diego', 'Elena', 'Esteban', 'Fernanda', 'Felipe',
  'Gabriela', 'Gonzalo', 'Isidora', 'Ignacio', 'Javiera', 'Joaquín', 'Karina',
  'Leandro', 'Macarena', 'Manuel', 'Natalia', 'Nicolás', 'Paulina', 'Pablo',
  'Rocío', 'Rodrigo', 'Sofía', 'Sebastián', 'Trinidad', 'Tomás', 'Valentina',
  'Vicente', 'Ximena', 'Álvaro', 'Antonia', 'Emilio',
];

const LAST_NAMES = [
  'Aguilera', 'Álvarez', 'Bravo', 'Bustos', 'Cárcamo', 'Castillo', 'Contreras',
  'Cortés', 'Donoso', 'Escobar', 'Farías', 'Fuentealba', 'Gallardo', 'Guzmán',
  'Herrera', 'Hidalgo', 'Ibarra', 'Jara', 'Lagos', 'Leiva', 'Maldonado',
  'Miranda', 'Navarro', 'Núñez', 'Olivares', 'Orellana', 'Palma', 'Peña',
  'Quezada', 'Riquelme', 'Sandoval', 'Sepúlveda', 'Tapia', 'Toro', 'Urrutia',
  'Valenzuela', 'Vergara', 'Villalobos', 'Yáñez', 'Zúñiga',
];

interface UnitDef {
  facultad: string;
  unidad: string;
  oecdArea: string;
  oecdSubarea: string;
}

const UNITS: UnitDef[] = [
  { facultad: 'Ingeniería', unidad: 'Departamento de Ingeniería Eléctrica', oecdArea: '5', oecdSubarea: '78' },
  { facultad: 'Ingeniería', unidad: 'Departamento de Ciencia de la Computación', oecdArea: '5', oecdSubarea: '78' },
  { facultad: 'Ingeniería', unidad: 'Departamento de Ingeniería Estructural y Geotécnica', oecdArea: '5', oecdSubarea: '78' },
  { facultad: 'Medicina', unidad: 'Escuela de Medicina', oecdArea: '3', oecdSubarea: '69' },
  { facultad: 'Medicina', unidad: 'Escuela de Enfermería', oecdArea: '3', oecdSubarea: '70' },
  { facultad: 'Ciencias Biológicas', unidad: 'Departamento de Ecología', oecdArea: '2', oecdSubarea: '67' },
  { facultad: 'Ciencias Biológicas', unidad: 'Departamento de Genética Molecular y Microbiología', oecdArea: '2', oecdSubarea: '67' },
  { facultad: 'Química y Farmacia', unidad: 'Escuela de Química y Farmacia', oecdArea: '2', oecdSubarea: '67' },
  { facultad: 'Ciencias Sociales', unidad: 'Escuela de Psicología', oecdArea: '4', oecdSubarea: '73' },
  { facultad: 'Ciencias Sociales', unidad: 'Instituto de Sociología', oecdArea: '4', oecdSubarea: '73' },
  { facultad: 'Economía y Administración', unidad: 'Departamento de Economía', oecdArea: '4', oecdSubarea: '73' },
  { facultad: 'Derecho', unidad: 'Escuela de Derecho', oecdArea: '4', oecdSubarea: '73' },
  { facultad: 'Historia, Geografía y Ciencia Política', unidad: 'Instituto de Historia', oecdArea: '4', oecdSubarea: '73' },
  { facultad: 'Agronomía y Sistemas Naturales', unidad: 'Departamento de Fruticultura y Enología', oecdArea: '1', oecdSubarea: '58' },
  { facultad: 'Física', unidad: 'Instituto de Física', oecdArea: '2', oecdSubarea: '67' },
];

const TOPIC_HEADS = [
  'Modelamiento predictivo de',
  'Caracterización experimental de',
  'Evaluación de impacto de',
  'Nuevas aproximaciones al estudio de',
  'Determinantes sociales de',
  'Optimización de procesos en',
  'Análisis longitudinal de',
  'Desarrollo de biomarcadores para',
  'Gobernanza y regulación de',
  'Restauración y monitoreo de',
];

const TOPIC_TAILS = [
  'la microbiota intestinal en poblaciones urbanas',
  'los sistemas de almacenamiento de energía de larga duración',
  'la adaptación de ecosistemas costeros al cambio climático',
  'las redes neuronales aplicadas a diagnóstico por imágenes',
  'la salud mental en estudiantes de educación superior',
  'los materiales cerámicos de alta resistencia',
  'la movilidad urbana en ciudades intermedias de Chile',
  'la resistencia antimicrobiana en entornos hospitalarios',
  'los mercados laborales en transición energética',
  'la conservación de humedales altoandinos',
  'la fotosíntesis en cultivos bajo estrés hídrico',
  'los sesgos algorítmicos en decisiones públicas',
  'las enfermedades neurodegenerativas de aparición temprana',
  'los sistemas agroforestales de secano',
  'la computación cuántica aplicada a optimización',
];

const KEYWORDS = [
  'salud;prevención;cohorte',
  'energía;almacenamiento;transición',
  'clima;adaptación;costero',
  'inteligencia artificial;imágenes;diagnóstico',
  'educación;bienestar;universidad',
  'materiales;cerámica;resistencia',
  'movilidad;ciudad;transporte',
  'microbiología;resistencia;hospital',
  'trabajo;economía;energía',
  'humedales;biodiversidad;conservación',
];

const pad = (value: number, length: number): string =>
  String(value).padStart(length, '0');

/**
 * Nombre + dos apellidos.
 *
 * Los tres índices se toman módulo 40, 39 y 37 — coprimos entre sí — así que por
 * el teorema chino del resto la terna no se repite hasta las 57.720 personas, y
 * las tres posiciones cambian en cada paso. (Descomponer el índice en base 40
 * no sirve: el dígito más significativo queda fijo en un lote de 170 y todos
 * terminan con el mismo apellido.) `salt` separa los rangos de investigadores,
 * evaluadores y co-investigadores.
 */
const nameFor = (index: number, salt: number): string => {
  const n = index + salt * 977;
  const first = FIRST_NAMES[n % FIRST_NAMES.length];
  const lastA = n % 39;
  const lastBRaw = n % 37;
  // Desplaza el segundo apellido para que nunca coincida con el primero, sin
  // perder inyectividad: el mapeo sigue siendo estrictamente creciente.
  const lastB = lastBRaw + (lastBRaw >= lastA ? 1 : 0);
  return `${first} ${LAST_NAMES[lastA]} ${LAST_NAMES[lastB % LAST_NAMES.length]}`;
};

const researcherId = (i: number): string =>
  `11111111-1111-4111-8111-9${pad(i, 11)}`;
const evaluatorId = (i: number): string =>
  `22222222-2222-4222-8222-9${pad(i, 11)}`;
const applicationId = (i: number): string =>
  `55555555-5555-4555-8555-9${pad(i, 11)}`;

/** Researchers who own the generated applications. */
const buildCohortResearchers = (): StoredUser[] =>
  Array.from({ length: COHORT_RESEARCHERS }, (_, i) => {
    const unit = UNITS[i % UNITS.length];
    return {
      id: researcherId(i),
      email: `ir.${pad(i, 3)}@uc.cl`,
      name: nameFor(i, 1),
      role: 'researcher' as const,
      isActive: true,
      isExternal: false,
      isConfirmed: true,
      createdAt: daysFromNow(-200 + (i % 60)),
      lastLoginAt: daysFromNow(-(i % 30) - 1),
      userProfile: {
        run: `${10 + (i % 15)}.${pad(100 + (i % 900), 3)}.${pad(i % 1000, 3)}-${i % 10}`,
        affiliation: 'Pontificia Universidad Católica de Chile',
        facultadUC: unit.facultad,
        unidadAcademica: unit.unidad,
        doctorateYear: 2005 + (i % 18),
        doctorateMonth: 1 + (i % 12),
        researcherType: i % 3 === 0 ? 'consolidated' : 'emerging',
        canApply: true,
      },
    };
  });

/**
 * The evaluator pool. With a quota of 6 per evaluator, 170 evaluators cover the
 * ~1.000 assignments the cohort needs, leaving room for arbitrajes.
 */
const buildCohortEvaluators = (): StoredUser[] =>
  Array.from({ length: COHORT_EVALUATORS }, (_, i) => {
    const unit = UNITS[i % UNITS.length];
    const isExternal = i % 5 === 0 || i % 7 === 0;
    return {
      id: evaluatorId(i),
      email: `evaluador.${pad(i, 3)}@${isExternal ? 'externo.cl' : 'uc.cl'}`,
      name: nameFor(i, 5),
      role: 'evaluator' as const,
      isActive: true,
      isExternal,
      // Uno de cada nueve queda sin confirmar: aparece como "Pendiente Configuración".
      isConfirmed: i % 9 !== 0,
      createdAt: daysFromNow(-180 + (i % 90)),
      lastLoginAt: i % 11 === 0 ? undefined : daysFromNow(-(i % 25) - 1),
      userProfile: {
        affiliation: isExternal
          ? 'Institución externa'
          : 'Pontificia Universidad Católica de Chile',
        facultadUC: unit.facultad,
        unidadAcademica: unit.unidad,
        researcherType: i % 4 === 0 ? 'emerging' : 'consolidated',
      },
    };
  });

/** 500 admissible applications, the batch the external company would match. */
const buildCohortApplications = (): StoredApplication[] =>
  Array.from({ length: COHORT_APPLICATIONS }, (_, i) => {
    const unit = UNITS[i % UNITS.length];
    const irIndex = i % COHORT_RESEARCHERS;
    const irName = nameFor(irIndex, 1);
    const title = `${TOPIC_HEADS[i % TOPIC_HEADS.length]} ${
      TOPIC_TAILS[(i * 3) % TOPIC_TAILS.length]
    }`;
    const submittedAt = daysFromNow(-45 + (i % 10));
    const admittedAt = daysFromNow(-20 + (i % 8));

    return {
      id: applicationId(i),
      userId: researcherId(irIndex),
      formTemplateId: T_OPEN,
      applicationNumber: `AV${FIRST_FOLIO + i}`,
      status: 'admissible' as const,
      sectionAnswers: {
        PROJECT_INFORMATION: {
          projectTitle: title,
          keywords: KEYWORDS[i % KEYWORDS.length],
          oecdArea: unit.oecdArea,
          oecdSubarea: unit.oecdSubarea,
          evaluationArea: unit.unidad,
          executionRegions: ['Región Metropolitana'],
        },
        PROJECT_TEAM: {
          principalInvestigator: {
            name: irName,
            email: `ir.${pad(irIndex, 3)}@uc.cl`,
            affiliation: 'Pontificia Universidad Católica de Chile',
            facultadUC: unit.facultad,
            unidadAcademicaUC: unit.unidad,
            type: irIndex % 3 === 0 ? 'consolidated' : 'emerging',
            role: 'IR',
            institutionType: 'Nacional',
            country: 'Chile',
          },
          coInvestigators: [
            {
              name: nameFor(i, 21),
              email: `coi.${pad(i, 3)}@uc.cl`,
              affiliation: 'Pontificia Universidad Católica de Chile',
              facultadUC: unit.facultad,
              type: 'consolidated',
              role: 'IP',
              institutionType: 'Nacional',
              country: 'Chile',
            },
          ],
        },
        PROJECT_RESOURCES: {
          budget: {
            montoSolicitado: 18_000_000 + (i % 12) * 1_000_000,
            montoContrapartes: 3_000_000 + (i % 5) * 500_000,
          },
        },
      },
      sectionStatus: {
        PROJECT_INFORMATION: 'completed' as const,
        PROJECT_TEAM: 'completed' as const,
        PROJECT_RESOURCES: 'completed' as const,
      },
      lastSaved: submittedAt,
      complianceConfirmed: true,
      submittedAt,
      createdAt: daysFromNow(-70 + (i % 15)),
      updatedAt: admittedAt,
      awardNotifiedAt: null,
      reviewerAssignment: {
        adminId: ADMIN_ID,
        adminName: 'Admin DINV',
        assignedAt: submittedAt,
      },
      statusHistory: [
        {
          status: 'submitted' as const,
          changedBy: ADMIN_ID,
          changedByName: 'Admin DINV',
          changedAt: submittedAt,
        },
        {
          status: 'admissible' as const,
          changedBy: ADMIN_ID,
          changedByName: 'Admin DINV',
          changedAt: admittedAt,
        },
      ],
    };
  });

/**
 * Assignments a few of these applications already had before the massive
 * import: DINV assigns some by hand, and one evaluator already declared a
 * conflict. Without them the wizard could never report "ya asignado" or
 * "conflicto previo" on this cohort.
 */
const buildCohortAssignments = (): StoredAssignment[] => {
  const preassigned: Array<{
    appIndex: number;
    evaluatorIndex: number;
    status: StoredAssignment['status'];
  }> = [
    { appIndex: 2, evaluatorIndex: 0, status: 'pending' },
    { appIndex: 2, evaluatorIndex: 1, status: 'accepted_not_started' },
    { appIndex: 7, evaluatorIndex: 2, status: 'conflict_declared' },
    { appIndex: 11, evaluatorIndex: 3, status: 'declined' },
    { appIndex: 14, evaluatorIndex: 4, status: 'pending' },
  ];

  return preassigned.map((item, i) => ({
    id: `66666666-6666-4666-8666-9${pad(i, 11)}`,
    applicationId: applicationId(item.appIndex),
    evaluatorId: evaluatorId(item.evaluatorIndex),
    assignedBy: ADMIN_ID,
    assignedAt: daysFromNow(-12 + i),
    // Estas se asignaron a mano y sí se notificaron: son el escenario "ya
    // asignado" del asistente, no "pendiente de invitar".
    invitationSentAt: daysFromNow(-12 + i),
    acceptedAt: item.status === 'accepted_not_started' ? daysFromNow(-10 + i) : null,
    status: item.status,
    evaluationSubmittedAt: null,
    conflictDeclared: item.status === 'conflict_declared',
    conflictStatus: item.status === 'conflict_declared' ? ('conflict_declared' as const) : null,
    conflictDetails:
      item.status === 'conflict_declared'
        ? 'Colaboración vigente con el equipo de investigación.'
        : null,
    conflictDeclaredAt: item.status === 'conflict_declared' ? daysFromNow(-9) : null,
    declineReason: item.status === 'declined' ? ('thematic' as const) : null,
    declineNote: item.status === 'declined' ? 'El tema no corresponde a mi área.' : null,
  }));
};

export const buildCohortUsers = (): StoredUser[] => [
  ...buildCohortResearchers(),
  ...buildCohortEvaluators(),
];

export const buildCohortApplicationRecords = buildCohortApplications;
export const buildCohortAssignmentRecords = buildCohortAssignments;
